import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  getSubmissionsGroupedByStudents,
  getSubmissionsByCourse,
  getSubmissionsByStudent,
  getSubmissionsGroupedBySubTopic,
} from './api';
import { ISubmission } from '../../SubmissionSlice';

interface ITopicForCourseSubmission {
  topicName: string;
  submissions: ISubmission[];
  averageScore: number;
  averageTime: number;
  _id: string;
}

export interface ICourseSubmissionsData {
  groupedSubmissions: ITopicForCourseSubmission[];
  totalAverageScore: number;
  totalAverageTime: number;
  courseId: string;
  courseTitle: string;
  studentsCount?: number;
}

export interface ITopicSubmission {
  topicName: string;
  submissions: ISubmission[];
  averageScore: number;
  averageTime: number;
  topicId: string;
  sortingOrder?: number;
}

export interface IStudentSubmissionsData {
  groupedSubmissions: ITopicSubmission[];
  totalAverageScore: number;
  totalAverageTime: number;
  studentId: string;
  firstname: string;
  lastname: string;
}

export interface IStudentPerformanceData {
  _id: string;
  firstname: string;
  lastname: string;
  avatar?: string;
  topics: ITopicSubmission[];
  averageScore: number;
  averageTime: number;
  lastSubmissionAt: string;
}

export interface ISubTopicSubmission {
  subTopicId: string;
  subTopicName: string;
  submissions: ISubmission[];
  averageScore: number;
  averageTime: number;
}

export interface ITopicGroupedSubmission {
  topicId: string;
  topicName: string;
  subTopics: ISubTopicSubmission[];
  curriculum: string;
  subject: string;
}

export interface ISubmissionsState {
  status: 'idle' | 'loading' | 'rejected';
  courseSubmissionsData: ICourseSubmissionsData | undefined;
  studentSubmissionsData: IStudentSubmissionsData | undefined;
  groupedStudentsSubmissions: IStudentPerformanceData[] | undefined;
  groupedSubTopicsSubmissions: ITopicGroupedSubmission[];
}

const initialState: ISubmissionsState = {
  status: 'idle',
  courseSubmissionsData: undefined,
  studentSubmissionsData: undefined,
  groupedStudentsSubmissions: undefined,
  groupedSubTopicsSubmissions: [],
};

export const SubmissionsSlice = createSlice({
  name: 'submissionsV2',
  initialState,
  reducers: {
    resetCourseSubmissionsData: (state) => {
      state.courseSubmissionsData = undefined;
    },
    resetStudentSubmissionsData: (state) => {
      state.studentSubmissionsData = undefined;
    },
    resetGroupedStudentsSubmissions: (state) => {
      state.groupedStudentsSubmissions = undefined;
    },
    resetGroupedSubTopicsSubmissions: (state) => {
      state.groupedSubTopicsSubmissions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // GET SUBMISSIONS BY COURSE
      .addCase(getSubmissionsByCourse.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseSubmissionsData = action.payload;
      })
      // GET SUBMISSIONS BY STUDENT
      .addCase(getSubmissionsByStudent.fulfilled, (state, action) => {
        state.status = 'idle';
        state.studentSubmissionsData = action.payload;
      })
      // GET SUBMISSIONS GROUPED BY STUDENTS
      .addCase(getSubmissionsGroupedByStudents.fulfilled, (state, action) => {
        state.status = 'idle';
        state.groupedStudentsSubmissions = action.payload;
      })
      // GET SUBMISSIONS GROUPED BY SUBTOPIC
      .addCase(getSubmissionsGroupedBySubTopic.fulfilled, (state, action) => {
        state.status = 'idle';
        const newItem = action.payload;
        state.groupedSubTopicsSubmissions = [
          ...state.groupedSubTopicsSubmissions,
          newItem,
        ];
      });

    const commonActions = [
      getSubmissionsByCourse,
      getSubmissionsByStudent,
      getSubmissionsGroupedByStudents,
      getSubmissionsGroupedBySubTopic,
    ] as const;

    builder
      // MATCHER
      .addMatcher(isPending(...commonActions), (state) => {
        state.status = 'loading';
      })
      .addMatcher(isRejected(...commonActions), (state) => {
        state.status = 'rejected';
      });
  },
});

export const getSubmissionsV2State = (
  state: RootState
): RootState['submissionsV2'] => state.submissionsV2;

export const {
  resetCourseSubmissionsData,
  resetStudentSubmissionsData,
  resetGroupedStudentsSubmissions,
  resetGroupedSubTopicsSubmissions,
} = SubmissionsSlice.actions;

export * from './api';
export default SubmissionsSlice.reducer;
